import {useMemo} from 'react';

import {FilterDialogSectionId} from 'filters/features/FilterDialog/types';
import {FilterTopbarItemId} from 'filters/features/FilterTopbar/types';
import {assertPolicyFilter, isPolicyFilter} from 'filters/types';

import {FeatureFlags} from 'core/constants/featureFlags';
import {useFeatureFlag} from 'core/hooks/authHooks';
import {useTeamsTreeViewSelection} from 'features/GlobalFilterProvider/filters/teams/useTeamsTreeViewSelection';
import {openFilterDialog} from 'filters';
import {createTreeViewSection} from 'filters/features/FilterDialog/sections/TreeViewSection/TreeViewSection';
import {createCounterItem} from 'filters/features/FilterTopbar/items/CounterItem/CounterItem';
import {useProducerTreeSeparated} from 'modules/decisionsNext/hooks/trees/userTrees/useProducerTree';
import {nullIfEmpty} from 'utils/arrayUtils';

import ProducersIcon from 'components/icons/metrics/producers.svg';

export const teamsDialogSection = createTreeViewSection({
  id: FilterDialogSectionId.Teams,
  title: 'Teams',
  icon: ProducersIcon,
  useTreeData: useProducerTreeSeparated,
  getDataOptions: value => ({
    skip: Object.values(value).every(group => !group?.length),
  }),
  useTreeSelection: useTeamsTreeViewSelection,
  getValue: filter => {
    assertPolicyFilter(filter);

    return {
      accountExecutives: filter.userFilters?.accountExecutiveIdsAndSourceCodes ?? undefined,
      accountManagers: filter.userFilters?.accountManagerIdsAndSourceCodes ?? undefined,
      producers: filter.userFilters?.producerIdsAndSourceCodes ?? undefined,
    };
  },
  onChange: (filter, value) => {
    assertPolicyFilter(filter);

    return {
      ...filter,
      userFilters: Object.values(value).every(group => !group?.length)
        ? undefined
        : {
            accountExecutiveIdsAndSourceCodes: value.accountExecutives,
            accountManagerIdsAndSourceCodes: value.accountManagers,
            producerIdsAndSourceCodes: value.producers,
          },
    };
  },
});

const createTeamsTopbarPair = ({
  entity,
  entitySingular,
  field,
  id,
  idAmsCodes,
}: {
  field: 'producerIdsAndSourceCodes' | 'accountManagerIdsAndSourceCodes' | 'accountExecutiveIdsAndSourceCodes';
  entity: string;
  entitySingular: string;
  id: FilterTopbarItemId;
  idAmsCodes: FilterTopbarItemId;
}) => [
  createCounterItem({
    id,
    entity,
    getCount: filter => {
      if (!isPolicyFilter(filter)) {
        return;
      }

      return filter.userFilters?.[field]?.filter(item => !item.sourceCodes?.length).length;
    },
    onClick: () => openFilterDialog(FilterDialogSectionId.Teams),
    onReset: filter => {
      assertPolicyFilter(filter);

      return {
        ...filter,
        userFilters: {
          ...filter.userFilters,
          [field]: nullIfEmpty(filter.userFilters?.[field]?.filter(item => item.sourceCodes?.length)),
        },
      };
    },
  }),
  createCounterItem({
    id: idAmsCodes,
    entity: `${entitySingular} AMS Codes`,
    getCount: filter => {
      if (!isPolicyFilter(filter)) {
        return;
      }

      return filter.userFilters?.[field]?.reduce((acc, item) => acc + (item.sourceCodes?.length || 0), 0);
    },
    onClick: () => openFilterDialog(FilterDialogSectionId.Teams),
    onReset: filter => {
      assertPolicyFilter(filter);

      return {
        ...filter,
        userFilters: {
          ...filter.userFilters,
          [field]: nullIfEmpty(filter.userFilters?.[field]?.filter(item => !item.sourceCodes?.length)),
        },
      };
    },
  }),
];

export const useTeamsTopbarItems = () => {
  const withAccountExecutives = useFeatureFlag(FeatureFlags.AccountExecutives);

  return useMemo(
    () =>
      [
        createTeamsTopbarPair({
          id: FilterTopbarItemId.Producer,
          idAmsCodes: FilterTopbarItemId.ProducerAmsCodes,
          entity: 'Producers',
          entitySingular: 'Producer',
          field: 'producerIdsAndSourceCodes',
        }),
        createTeamsTopbarPair({
          id: FilterTopbarItemId.AccountManager,
          idAmsCodes: FilterTopbarItemId.AccountManagerAmsCodes,
          entity: 'Account Managers',
          entitySingular: 'Account Manager',
          field: 'accountManagerIdsAndSourceCodes',
        }),
        withAccountExecutives &&
          createTeamsTopbarPair({
            id: FilterTopbarItemId.AccountExecutive,
            idAmsCodes: FilterTopbarItemId.AccountExecutiveAmsCodes,
            entity: 'Account Executives',
            entitySingular: 'Account Executive',
            field: 'accountExecutiveIdsAndSourceCodes',
          }),
      ].flat(),
    [withAccountExecutives]
  );
};
